import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Heart,
  Trophy,
  Droplets,
  Send,
  MessageCircle,
  ArrowLeft,
  Copy,
  Check,
  User,
  Link2,
  AlertCircle,
  Eye
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { getProgress, getWaterIntake, getNotes, saveNote, linkPartner } from "./partnerApi";
import { supabase } from "./supabaseClient";

export default function PartnerDashboard({ onBack, standalone, viewLaibaPOV, setViewLaibaPOV }) {
  const { user, profile } = useAuth();
  const [partner, setPartner] = useState(null);
  const [partnerCode, setPartnerCode] = useState("");
  const [todayProgress, setTodayProgress] = useState({});
  const [todayWater, setTodayWater] = useState(0);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [copied, setCopied] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadData = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    console.log('=== Partner Dashboard Load ===');
    console.log('User:', user?.id);
    console.log('Profile:', profile);
    console.log('Partner ID:', profile?.partner_id);
    
    try {
      // Get partner info
      if (profile?.partner_id) {
        console.log('Fetching partner with ID:', profile.partner_id);
        const { data: partnerProfile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', profile.partner_id)
          .single();
        
        console.log('Partner profile:', partnerProfile);
        console.log('Partner fetch error:', error);
        
        if (partnerProfile) {
          setPartner(partnerProfile);
          
          // Fetch partner's progress
          const partnerProg = await getProgress(partnerProfile.id, today);
          console.log('Partner progress:', partnerProg);
          setTodayProgress(partnerProg || {});
          
          const water = await getWaterIntake(partnerProfile.id, today);
          console.log('Partner water:', water);
          setTodayWater(water || 0);
        }
      } else {
        console.log('No partner_id in profile');
      }
      
      // Fetch notes
      const notesData = await getNotes(user.id);
      setNotes(notesData || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, today, profile?.partner_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLinkPartner = async () => {
    if (!partnerCode.trim()) return;
    
    setLinking(true);
    setLinkError("");
    
    console.log('=== Partner Link Debug ===');
    console.log('User ID:', user?.id);
    console.log('Partner Code:', partnerCode.trim().toUpperCase());
    
    try {
      const result = await linkPartner(user.id, partnerCode.trim().toUpperCase());
      console.log('Link result:', result);
      
      if (result.success) {
        setLinkSuccess(true);
        setPartnerCode("");
        // Reload after short delay
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setLinkError(result.error || "Failed to link partner");
      }
    } catch (err) {
      console.error('Link error:', err);
      setLinkError("Failed to link partner");
    } finally {
      setLinking(false);
    }
  };

  const handleSendNote = async () => {
    if (!newNote.trim() || !partner?.id) return;
    
    setSending(true);
    try {
      await saveNote(user.id, partner.id, newNote.trim());
      setNewNote("");
      const recentNotes = await getNotes(user.id);
      setNotes(recentNotes || []);
    } catch (err) {
      console.error("Error sending note:", err);
    } finally {
      setSending(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(profile?.partner_code || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const quests = [
    { key: 'water', label: 'Drink 8 cups water' },
    { key: 'breakfast', label: 'Healthy breakfast' },
    { key: 'lunch', label: 'Nutritious lunch' },
    { key: 'dinner', label: 'Balanced dinner' },
    { key: 'exercise', label: 'Movement quest' },
    { key: 'mindfulness', label: 'Mindful moment' },
  ];

  const completedCount = todayProgress ? Object.values(todayProgress).filter(Boolean).length : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 pt-6 pb-16 px-6">
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/80 hover:text-white transition"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          
          {/* View Laiba's POV Button - Only for partner */}
          {profile?.role === 'partner' && (
            <button
              onClick={() => setViewLaibaPOV(!viewLaibaPOV)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition"
            >
              <Eye className="w-4 h-4" />
              <span>{viewLaibaPOV ? "Back to Partner View" : "View Laiba's POV"}</span>
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-3 mt-4">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Partner Mode</h1>
            <p className="text-white/80 text-sm">
              {profile?.role === 'partner' ? `Supporting ${partner?.name || 'Laiba'}` : 'Support & encourage each other'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 -mt-10 space-y-4 pb-8">
        {/* Partner Code - ONLY for Laiba (not partner role) */}
        {profile?.role !== 'partner' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-lg p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <Link2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Your Partner Code</h3>
                <p className="text-sm text-slate-500">Share this with your supporter</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4">
              <span className="text-2xl font-mono font-bold text-indigo-600 tracking-wider">
                {profile?.partner_code || "Loading..."}
              </span>
              <button
                onClick={copyCode}
                className="ml-auto p-2 bg-white rounded-xl shadow-sm hover:shadow-md transition"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <Copy className="w-5 h-5 text-slate-500" />
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Link Partner Section - Enter Laiba's code (for partner role) */}
        {profile?.role === 'partner' && !partner && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl shadow-lg p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Connect with Laiba</h3>
                <p className="text-sm text-slate-500">Enter her partner code to link</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <input
                type="text"
                value={partnerCode}
                onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
                placeholder="Enter Laiba's code"
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition font-mono uppercase"
                maxLength={6}
              />
              <button
                onClick={handleLinkPartner}
                disabled={linking || !partnerCode.trim()}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition disabled:opacity-50"
              >
                {linking ? "Linking..." : "Link"}
              </button>
            </div>
            
            {linkError && (
              <div className="flex items-center gap-2 mt-3 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                {linkError}
              </div>
            )}
            
            {linkSuccess && (
              <div className="flex items-center gap-2 mt-3 text-green-500 text-sm">
                <Check className="w-4 h-4" />
                Connected to Laiba!
              </div>
            )}
          </motion.div>
        )}

        {/* Partner's Progress */}
        {partner && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl shadow-lg p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">{partner.name || "Partner"}'s Today</h3>
                  <p className="text-sm text-slate-500">Daily progress at a glance</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    <span className="text-sm text-slate-600">Quests</span>
                  </div>
                  <div className="text-3xl font-black text-amber-600">{completedCount}/6</div>
                </div>
                <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Droplets className="w-5 h-5 text-sky-500" />
                    <span className="text-sm text-slate-600">Water</span>
                  </div>
                  <div className="text-3xl font-black text-sky-600">{todayWater}/8</div>
                </div>
              </div>

              {/* Quest Checklist */}
              <div className="space-y-2">
                {quests.map((quest) => (
                  <div
                    key={quest.key}
                    className={`flex items-center gap-3 p-3 rounded-xl transition ${
                      todayProgress[quest.key]
                        ? "bg-green-50 text-green-700"
                        : "bg-slate-50 text-slate-500"
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      todayProgress[quest.key] ? "bg-green-500" : "bg-slate-200"
                    }`}>
                      {todayProgress[quest.key] && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-sm font-medium">{quest.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Send Encouragement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl shadow-lg p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                  <Send className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Send Love Note</h3>
                  <p className="text-sm text-slate-500">Encourage your partner</p>
                </div>
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="You're doing amazing! 💕"
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition"
                />
                <button
                  onClick={handleSendNote}
                  disabled={sending || !newNote.trim()}
                  className="px-4 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </motion.div>

            {/* Recent Notes */}
            {notes.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-3xl shadow-lg p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">Recent Notes</h3>
                    <p className="text-sm text-slate-500">Messages from you</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {notes.slice(0, 5).map((note) => (
                    <div key={note.id} className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-4">
                      <p className="text-slate-700">{note.message}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </>
        )}

        {/* No Partner Message */}
        {!partner && !loading && profile?.role === 'partner' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-pink-100 to-rose-100 rounded-3xl p-8 text-center"
          >
            <Heart className="w-12 h-12 text-rose-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-800 mb-2">Connect with Laiba!</h3>
            <p className="text-slate-600 mb-4">
              Enter Laiba's partner code above to link your accounts.
            </p>
            <p className="text-sm text-slate-500">
              Once linked, you'll see her daily progress and can send encouragement! 💕
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}